import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { trackEvent } from '@/lib/analytics';

const TOOL_URL = '/world-cup-brand-index-tool.html';
const SPRINT_URL = '/briefing';
const PREDICTOR_URL = '/predictor';
const API = process.env.REACT_APP_BACKEND_URL;

/* ═══ Color Tokens ═══ */
const C = {
  bg: '#131619',
  surface: '#1A1E23',
  card: '#1A1E23',
  blue: '#4389D0',
  blueHover: '#3574B8',
  lightBlue: '#5BA3F5',
  green: '#2D9B65',
  amber: '#CC8B12',
  amberDim: '#1E1304',
  red: '#D94444',
  text: '#E4E7EC',
  textSec: '#8B929E',
  border: '#2A2F36',
  footer: '#131619',
  dimBorder: '#2A2F36',
};

/* ═══ Translations — Simplified ═══ */
const T = {
  en: {
    tickerBadge: 'YBV INDEX™',
    tickerLive: 'LIVE',
    heroBadge: 'WORLD CUP BRAND INDEX™ · 2026',
    heroH1: 'Which national team is worth the most?',
    heroDesc: 'The ranking that measures which nations are creating the most value during the World Cup. Not goals. Not FIFA rankings. Brand value.',
    heroCta: 'EXPLORE THE RANKING →',
    heroSub: 'Free · No sign-up required',
    heroStats: ['48 NATIONS', '6 DIMENSIONS', 'UPDATED DAILY'],
    divH2: 'The best team on the pitch is not always the most valuable brand.',
    divColFifa: 'FIFA RANK',
    divColBrand: 'BRAND RANK',
    divFooter: 'Why is Argentina #1 in brand but #3 in FIFA? Why is Spain climbing? The Brand Index tells you.',
    compare: [
      { fifa: '#1', flag: '🇫🇷', name: 'France', brand: '#3', dir: 'down' },
      { fifa: '#3', flag: '🇦🇷', name: 'Argentina', brand: '#1', dir: 'up' },
      { fifa: '#6', flag: '🇧🇷', name: 'Brazil', brand: '#2', dir: 'up' },
      { fifa: '#18', flag: '🇺🇸', name: 'USA', brand: '#9', dir: 'up' },
      { fifa: '#27', flag: '🇪🇸', name: 'Spain', brand: '#4', dir: 'up' },
    ],
    predBadge: 'BRAND CHAMPION PREDICTOR™',
    predH2: 'Who will be the most valuable brand after the World Cup?',
    predDesc: 'Make your prediction and compare it with football fans from around the world.',
    predCta: 'MAKE YOUR PREDICTION →',
    predFrom: 'predictions from',
    predCountries: 'countries',
    sigH2: 'Every match changes the ranking.',
    signals: [
      { type: 'green', flag: '🇦🇷', date: '11 Jun', text: 'Messi confirms his 6th World Cup', pct: '+2.1%' },
      { type: 'green', flag: '🇧🇷', date: '08 Jun', text: 'Neymar returns to World Cup squad', pct: '+3.2%' },
      { type: 'green', flag: '🇪🇸', date: '10 Jun', text: 'Spain names youngest squad in history', pct: '+1.4%' },
      { type: 'green', flag: '🇺🇸', date: '01 Jun', text: 'USA gains commercial momentum ahead of 2026', pct: '+2.8%' },
    ],
    sigFooter: 'Live signals are activated progressively as verified media, match and search data become available. We only display source-backed signals.',
    ctaH2: 'How much is your nation worth?',
    ctaDesc: 'Explore the full ranking, compare nations and simulate the impact of any tournament scenario.',
    ctaCta: 'EXPLORE THE RANKING →',
    ctaLink: 'Want the full analysis for your organisation? → Contact us',
    ctaCredibility: 'Built by YBV Sports using 50+ signals including sporting performance, media visibility, search interest and sponsorship attractiveness.',
    footerUrl: 'sports.yourbrandvalue.com',
    footerProduct: 'World Cup Brand Index™ 2026',
    footerCopy: '© 2026 Your Brand Value Sports',
    disclaimer: 'World Cup Brand Index™ 2026 is an independent brand intelligence and research tool developed by Your Brand Value Sports. It is not affiliated with, sponsored by, endorsed by, or officially connected to FIFA, the FIFA World Cup™, any national football federation, or any official tournament partner. All brand scores, rankings, valuations and projections are proprietary analytical estimates generated by Your Brand Value Sports and are provided for informational purposes only.',
    faqTitle: 'Frequently Asked Questions',
    faq: [
      { q: 'What is the World Cup Brand Index™?', a: 'An independent brand intelligence ranking that evaluates the commercial strength and brand value of national football teams.' },
      { q: 'Is this an official FIFA ranking?', a: 'No. World Cup Brand Index™ is an independent brand intelligence tool developed by Your Brand Value Sports and is not affiliated with, sponsored by or endorsed by FIFA.' },
      { q: 'How is the World Cup Brand Index™ calculated?', a: 'The World Cup Brand Index™ uses a proprietary brand intelligence methodology developed by Your Brand Value Sports. The model evaluates multiple dimensions including fan engagement, media visibility, sponsorship appeal, digital influence, commercial strength and competitive momentum to estimate the commercial value and brand strength of national football teams.' },
      { q: 'Does the ranking measure sporting performance?', a: 'Yes, but it is only one of the factors considered. World Cup Brand Index™ primarily evaluates commercial strength and brand value, combining fan engagement, media visibility, sponsorship appeal, digital influence and competitive momentum.' },
      { q: 'Why can two teams with similar sporting results have different rankings?', a: 'Because brand value depends on many factors beyond results on the pitch, including fan base size, commercial attractiveness, media visibility, sponsorship appeal and digital engagement.' },
      { q: 'Are the scores official valuations?', a: 'No. Scores, rankings and projections are analytical estimates generated through proprietary models for informational and strategic purposes. They should not be considered official financial valuations.' },
      { q: 'How often is it updated?', a: 'Rankings and verified signals are updated as new verified data becomes available.' },
    ],
    ticker: '🇦🇷 Argentina 83 +2.1%   ·   🇧🇷 Brazil 82 0%   ·   🇫🇷 France 81 -1.8%   ·   🇪🇸 Spain 78 +5.8%   ·   🇬🇧 England 76 +0.8%   ·   🇵🇹 Portugal 74 +2.4%   ·   🇩🇪 Germany 73 -2.6%   ·   🇧🇪 Belgium 72 -1.6%   ·   🇺🇸 USA 71 +4.8%   ·   🇳🇴 Norway 64 +2.2%',
  },
  es: {
    tickerBadge: 'YBV INDEX™',
    tickerLive: 'EN VIVO',
    heroBadge: 'WORLD CUP BRAND INDEX™ · 2026',
    heroH1: '¿Qué selección vale más?',
    heroDesc: 'El ranking que mide qué naciones están creando más valor durante el Mundial. No son goles. No es el ranking FIFA. Es valor de marca.',
    heroCta: 'EXPLORAR EL RANKING →',
    heroSub: 'Gratis · Sin registro',
    heroStats: ['48 NACIONES', '6 DIMENSIONES', 'ACTUALIZADO A DIARIO'],
    divH2: 'El mejor equipo en el campo no siempre es la marca más valiosa.',
    divColFifa: 'RANKING FIFA',
    divColBrand: 'RANKING MARCA',
    divFooter: '¿Por qué Argentina es #1 en marca pero #3 en FIFA? ¿Por qué España sube? El Brand Index te lo dice.',
    compare: [
      { fifa: '#1', flag: '🇫🇷', name: 'Francia', brand: '#3', dir: 'down' },
      { fifa: '#3', flag: '🇦🇷', name: 'Argentina', brand: '#1', dir: 'up' },
      { fifa: '#6', flag: '🇧🇷', name: 'Brasil', brand: '#2', dir: 'up' },
      { fifa: '#18', flag: '🇺🇸', name: 'EE.UU.', brand: '#9', dir: 'up' },
      { fifa: '#27', flag: '🇪🇸', name: 'España', brand: '#4', dir: 'up' },
    ],
    predBadge: 'BRAND CHAMPION PREDICTOR™',
    predH2: '¿Quién será la marca más valiosa tras el Mundial?',
    predDesc: 'Haz tu predicción y compárala con fans del fútbol de todo el mundo.',
    predCta: 'HAZ TU PREDICCIÓN →',
    predFrom: 'predicciones de',
    predCountries: 'países',
    sigH2: 'Cada partido cambia el ranking.',
    signals: [
      { type: 'green', flag: '🇦🇷', date: '11 jun', text: 'Messi confirma su 6º Mundial', pct: '+2.1%' },
      { type: 'green', flag: '🇧🇷', date: '08 jun', text: 'Neymar regresa a la convocatoria mundialista', pct: '+3.2%' },
      { type: 'green', flag: '🇪🇸', date: '10 jun', text: 'Selección más joven de la historia', pct: '+1.4%' },
      { type: 'green', flag: '🇺🇸', date: '01 jun', text: 'EE.UU. gana impulso comercial rumbo a 2026', pct: '+2.8%' },
    ],
    sigFooter: 'Las señales se activan progresivamente a medida que hay datos verificados de medios, partidos y búsquedas. Solo mostramos señales respaldadas por fuentes reales.',
    ctaH2: '¿Cuánto vale tu selección?',
    ctaDesc: 'Explora el ranking completo, compara naciones y simula el impacto de cualquier escenario del torneo.',
    ctaCta: 'EXPLORAR EL RANKING →',
    ctaLink: '¿Quieres el análisis completo para tu organización? → Contacta con nosotros',
    ctaCredibility: 'Creado por YBV Sports usando más de 50 señales: rendimiento deportivo, visibilidad mediática, interés de búsqueda y atractivo para sponsors.',
    footerUrl: 'sports.yourbrandvalue.com',
    footerProduct: 'World Cup Brand Index™ 2026',
    footerCopy: '© 2026 Your Brand Value Sports',
    disclaimer: 'World Cup Brand Index™ 2026 es una herramienta independiente de inteligencia e investigación de marca desarrollada por Your Brand Value Sports. No está afiliada, patrocinada, avalada ni vinculada oficialmente con FIFA, la Copa Mundial de la FIFA™, ninguna federación nacional de fútbol ni ningún patrocinador oficial del torneo. Todos los Brand Scores, rankings, valoraciones y proyecciones son estimaciones analíticas propias de Your Brand Value Sports y se ofrecen únicamente con fines informativos.',
    faqTitle: 'Preguntas Frecuentes',
    faq: [
      { q: '¿Qué es World Cup Brand Index™?', a: 'Un ranking independiente de inteligencia de marca que evalúa la fortaleza comercial y el valor de marca de las selecciones nacionales de fútbol.' },
      { q: '¿Es un ranking oficial de FIFA?', a: 'No. World Cup Brand Index™ es una herramienta independiente de inteligencia de marca desarrollada por Your Brand Value Sports y no está afiliada, patrocinada ni avalada por FIFA.' },
      { q: '¿Cómo se calcula World Cup Brand Index™?', a: 'World Cup Brand Index™ utiliza una metodología propia de inteligencia de marca desarrollada por Your Brand Value Sports. El modelo evalúa múltiples dimensiones como engagement de aficionados, visibilidad mediática, atractivo para patrocinadores, influencia digital, fortaleza comercial y momentum competitivo para estimar el valor comercial y la fortaleza de marca de las selecciones nacionales.' },
      { q: '¿El ranking mide el rendimiento deportivo?', a: 'Sí, pero es solo uno de los factores considerados. World Cup Brand Index™ evalúa principalmente la fortaleza comercial y el valor de marca, combinando variables como engagement de aficionados, visibilidad mediática, atractivo para patrocinadores, influencia digital y momentum competitivo.' },
      { q: '¿Por qué dos selecciones con resultados similares pueden tener posiciones diferentes?', a: 'Porque el valor de marca depende de múltiples factores más allá del rendimiento deportivo, incluyendo tamaño de la base de aficionados, atractivo comercial, visibilidad mediática, capacidad de patrocinio y engagement digital.' },
      { q: '¿Las puntuaciones son valoraciones oficiales?', a: 'No. Las puntuaciones, rankings y proyecciones son estimaciones analíticas generadas mediante modelos propios con fines informativos y estratégicos. No deben considerarse valoraciones financieras oficiales.' },
      { q: '¿Con qué frecuencia se actualiza?', a: 'Los rankings y señales verificadas se actualizan a medida que se dispone de nuevos datos verificados.' },
    ],
    ticker: '🇦🇷 Argentina 83 +2.1%   ·   🇧🇷 Brasil 82 0%   ·   🇫🇷 Francia 81 -1.8%   ·   🇪🇸 España 78 +5.8%   ·   🇬🇧 Inglaterra 76 +0.8%   ·   🇵🇹 Portugal 74 +2.4%   ·   🇩🇪 Alemania 73 -2.6%   ·   🇧🇪 Bélgica 72 -1.6%   ·   🇺🇸 EE.UU. 71 +4.8%   ·   🇳🇴 Noruega 64 +2.2%',
  },
};

const signalColor = (t) => t === 'green' ? C.green : t === 'red' ? C.red : C.amber;

/* ═══ Global Styles ═══ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
@keyframes scrollX {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}
.wcbi-fadein { opacity: 0; animation: fadeUp 0.7s ease forwards; }
.wcbi-btn:hover { background: ${C.blueHover} !important; box-shadow: 0 0 24px rgba(55,121,197,0.35) !important; }
.wcbi-row:hover { background: ${C.surface} !important; }
.wcbi-link:hover { color: ${C.text} !important; }
.wcbi-lang:hover { background: rgba(67,137,208,0.15) !important; }
`;

const syne = (w = 700) => ({ fontFamily: 'Inter, sans-serif', fontWeight: w });
const mono = (w = 400) => ({ fontFamily: 'Inter, sans-serif', fontWeight: w >= 500 ? 600 : 500, fontVariantNumeric: 'tabular-nums' });
const sans = (w = 400) => ({ fontFamily: 'Inter, sans-serif', fontWeight: w });

export default function WorldCupLanding() {
  const { lang, setLang } = useLanguage();
  const t = T[lang] || T.en;
  const [predStats, setPredStats] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    // SEO meta tags
    const metaTitle = lang === 'es' ? 'World Cup Brand Index™ 2026 | Ranking de Marca de Selecciones Nacionales' : 'World Cup Brand Index™ 2026 | National Team Brand Ranking';
    const metaDesc = lang === 'es' ? 'Descubre World Cup Brand Index™ 2026, un ranking independiente que mide la fortaleza comercial de las selecciones nacionales mediante inteligencia de marca, atractivo para patrocinadores, visibilidad mediática e influencia digital.' : 'Discover the World Cup Brand Index™ 2026, an independent ranking that measures the commercial strength of national football teams through brand intelligence, sponsorship appeal, media visibility and digital influence.';
    const ogTitle = 'World Cup Brand Index™ 2026 | Independent Brand Ranking of National Teams';
    const ogDesc = 'An independent brand intelligence ranking measuring the commercial strength of national football teams.';
    document.title = metaTitle;
    const setMeta = (name, content, prop) => { let el = document.querySelector(`meta[${prop || 'name'}="${name}"]`); if (!el) { el = document.createElement('meta'); el.setAttribute(prop || 'name', name); document.head.appendChild(el); } el.setAttribute('content', content); };
    setMeta('description', metaDesc);
    setMeta('og:title', ogTitle, 'property');
    setMeta('og:description', ogDesc, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:url', 'https://sports.yourbrandvalue.com/world-cup-brand-index', 'property');
    setMeta('og:site_name', 'Your Brand Value Sports', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle);
    setMeta('twitter:description', ogDesc);
    const els = document.querySelectorAll('.wcbi-observe');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('wcbi-fadein'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    fetch(`${API}/api/predictions/results`).then(r => r.json()).then(setPredStats).catch(() => {});
  }, []);

  const toolUrl = lang === 'es' ? `${TOOL_URL}?lang=es` : TOOL_URL;

  useEffect(() => { trackEvent('landing_view', { landing_type: 'wcbi_landing', language: lang }); }, []);

  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{GLOBAL_CSS}</style>

      {/* ═══ TICKER ═══ */}
      <div style={{ height: 28, background: C.surface, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', zIndex: 10 }}>
        <div style={{ ...mono(500), fontSize: 8, color: C.blue, background: '#1A2840', padding: '2px 8px', borderRadius: 3, marginLeft: 12, flexShrink: 0, letterSpacing: 1 }}>{t.tickerBadge}</div>
        <div style={{ flex: 1, overflow: 'hidden', margin: '0 12px' }}>
          <div style={{ display: 'flex', animation: 'scrollX 45s linear infinite', whiteSpace: 'nowrap' }}>
            {[0, 1].map(i => <span key={i} style={{ ...mono(), fontSize: 9, color: C.textSec, paddingRight: 60 }}>{t.ticker}</span>)}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 12, flexShrink: 0 }}>
          <div style={{ display: 'flex', borderRadius: 3, overflow: 'hidden', border: `1px solid ${C.border}`, marginRight: 6 }} data-testid="lang-toggle">
            {['en', 'es'].map(l => (
              <button key={l} className="wcbi-lang" onClick={() => setLang(l)} data-testid={`lang-${l}`}
                style={{ ...mono(500), fontSize: 7, padding: '1px 6px', border: 'none', cursor: 'pointer', letterSpacing: '0.08em',
                  background: lang === l ? C.blue : 'transparent', color: lang === l ? '#fff' : C.textSec, transition: 'background 0.15s, color 0.15s' }}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, animation: 'blink 2s infinite' }} />
          <span style={{ ...mono(), fontSize: 9, color: C.green }}>{t.tickerLive}</span>
        </div>
      </div>

      {/* ═══ HERO — Clear, simple, curiosity-driven ═══ */}
      <section style={{ minHeight: 'calc(100vh - 28px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ ...mono(500), fontSize: 9, color: C.blue, border: `1px solid ${C.blue}`, background: '#1A2840', padding: '5px 16px', borderRadius: 20, letterSpacing: 2, marginBottom: 32 }}>
          {t.heroBadge}
        </div>
        <h1 style={{ ...syne(800), fontSize: 'clamp(34px, 5.5vw, 58px)', lineHeight: 1.08, color: '#fff', maxWidth: 650, margin: 0 }}>
          {t.heroH1}
        </h1>
        <p style={{ ...sans(), fontSize: 17, color: C.textSec, maxWidth: 520, marginTop: 24, lineHeight: 1.7 }}>
          {t.heroDesc}
        </p>
        <div style={{ ...mono(), fontSize: 11, marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          {t.heroStats.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: C.textSec }}>·</span>}
              <span style={{ color: i === 0 ? '#fff' : i === 1 ? C.blue : C.textSec }}>{s}</span>
            </React.Fragment>
          ))}
        </div>
        <a href={toolUrl} className="wcbi-btn" style={{ ...mono(600), fontSize: 14, color: '#fff', background: C.blue, padding: '16px 40px', borderRadius: 6, textDecoration: 'none', marginTop: 40, display: 'inline-block', transition: 'background 0.15s, box-shadow 0.15s', boxShadow: '0 0 20px rgba(55,121,197,0.3)' }} data-testid="hero-cta">
          {t.heroCta}
        </a>
        <p style={{ ...sans(), fontSize: 11, color: C.textSec, marginTop: 12 }}>{t.heroSub}</p>
      </section>

      {/* ═══ DIVERGENCE — Creates curiosity ═══ */}
      <section className="wcbi-observe" style={{ background: C.surface, padding: '72px 20px', opacity: 0 }}>
        <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ ...syne(700), fontSize: 'clamp(20px, 3vw, 26px)', color: '#fff', maxWidth: 520, margin: '0 auto', lineHeight: 1.25 }}>
            {t.divH2}
          </h2>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, maxWidth: 480, margin: '32px auto 0', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ ...mono(), fontSize: 9, color: C.textSec, textAlign: 'left' }}>{t.divColFifa}</span>
              <span />
              <span style={{ ...mono(), fontSize: 9, color: C.textSec, textAlign: 'right' }}>{t.divColBrand}</span>
            </div>
            {t.compare.map((r, i) => (
              <div key={i} className="wcbi-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', padding: '10px 16px', borderBottom: i < t.compare.length - 1 ? `1px solid ${C.border}` : 'none', transition: 'background 0.15s' }}>
                <span style={{ ...mono(500), fontSize: 13, color: C.textSec, textAlign: 'left' }}>{r.fifa}</span>
                <span style={{ ...syne(600), fontSize: 14, color: '#fff', textAlign: 'center' }}>{r.flag} {r.name}</span>
                <span style={{ ...mono(500), fontSize: 13, color: r.dir === 'up' ? C.green : C.red, textAlign: 'right' }}>
                  {r.brand} {r.dir === 'up' ? '↑' : '↓'}
                </span>
              </div>
            ))}
          </div>
          <p style={{ ...sans(), fontSize: 14, color: C.textSec, maxWidth: 480, margin: '24px auto 0', lineHeight: 1.7 }}>
            {t.divFooter}
          </p>
        </div>
      </section>

      {/* ═══ PREDICTOR — Prominent conversion element ═══ */}
      <section className="wcbi-observe" style={{ background: C.bg, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: '72px 20px', opacity: 0 }}>
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ ...mono(500), fontSize: 9, color: C.amber, border: `1px solid ${C.amber}`, background: C.amberDim, padding: '5px 16px', borderRadius: 20, letterSpacing: '0.12em', display: 'inline-block', marginBottom: 24 }}>
            {t.predBadge}
          </div>
          <h2 style={{ ...syne(700), fontSize: 'clamp(22px, 3.5vw, 30px)', color: '#fff', lineHeight: 1.2, marginBottom: 12 }}>
            {t.predH2}
          </h2>
          <p style={{ ...sans(), fontSize: 15, color: C.textSec, lineHeight: 1.6, maxWidth: 440, margin: '0 auto' }}>
            {t.predDesc}
          </p>
          <Link to={PREDICTOR_URL} className="wcbi-btn" style={{ ...mono(600), fontSize: 14, color: '#fff', background: C.amber, padding: '16px 40px', borderRadius: 6, textDecoration: 'none', marginTop: 32, display: 'inline-block', transition: 'background 0.15s, box-shadow 0.15s', boxShadow: '0 0 20px rgba(204,139,18,0.3)' }} data-testid="predictor-cta">
            {t.predCta}
          </Link>
          {predStats && predStats.total_predictions > 0 && (
            <div style={{ ...mono(), fontSize: 12, color: C.amber, marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, animation: 'blink 2s infinite' }} />
              {predStats.total_predictions} {t.predFrom} {predStats.countries_count} {t.predCountries}
            </div>
          )}
        </div>
      </section>

      {/* ═══ SIGNALS — Proof the product is alive ═══ */}
      <section className="wcbi-observe" style={{ background: C.surface, padding: '72px 20px', opacity: 0 }}>
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ ...syne(700), fontSize: 'clamp(20px, 3vw, 26px)', color: '#fff', marginBottom: 28 }}>
            {t.sigH2}
          </h2>
          <div style={{ textAlign: 'left' }}>
            {t.signals.map((s, i) => (
              <div key={i} style={{ borderLeft: `3px solid ${signalColor(s.type)}`, padding: '10px 14px', marginBottom: 8, background: 'rgba(255,255,255,0.02)', borderRadius: '0 6px 6px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <span style={{ ...mono(), fontSize: 10, color: C.textSec }}>{s.flag} · {s.date}</span>
                    <span style={{ ...sans(), fontSize: 13, color: C.text, marginLeft: 8 }}>{s.text}</span>
                  </div>
                  <span style={{ ...mono(500), fontSize: 12, color: signalColor(s.type) }}>{s.pct}</span>
                </div>
              </div>
            ))}
          </div>
          <p style={{ ...mono(), fontSize: 10, color: C.textSec, marginTop: 20 }}>{t.sigFooter}</p>
        </div>
      </section>

      {/* ═══ FINAL CTA + Credibility one-liner ═══ */}
      <section id="access" className="wcbi-observe" style={{ background: C.bg, borderTop: `1px solid ${C.border}`, padding: '80px 20px', textAlign: 'center', opacity: 0 }}>
        <h2 style={{ ...syne(800), fontSize: 'clamp(28px, 4vw, 38px)', color: '#fff', maxWidth: 480, margin: '0 auto', lineHeight: 1.1 }}>
          {t.ctaH2}
        </h2>
        <p style={{ ...sans(), fontSize: 15, color: C.textSec, maxWidth: 440, margin: '16px auto 0', lineHeight: 1.6 }}>
          {t.ctaDesc}
        </p>
        <a href={toolUrl} className="wcbi-btn" style={{ ...mono(600), fontSize: 14, color: '#fff', background: C.blue, padding: '16px 44px', borderRadius: 6, textDecoration: 'none', marginTop: 36, display: 'inline-block', transition: 'background 0.15s, box-shadow 0.15s', boxShadow: '0 0 20px rgba(55,121,197,0.3)' }} data-testid="final-cta">
          {t.ctaCta}
        </a>
        <div style={{ marginTop: 14 }}>
          <Link to={SPRINT_URL} className="wcbi-link" style={{ ...sans(), fontSize: 12, color: C.textSec, textDecoration: 'none', transition: 'color 0.15s' }}>
            {t.ctaLink}
          </Link>
        </div>
        <p style={{ ...sans(), fontSize: 11, color: C.dimBorder, maxWidth: 460, margin: '32px auto 0', lineHeight: 1.6 }}>
          {t.ctaCredibility}
        </p>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="wcbi-observe" style={{ background: C.surface, borderTop: `1px solid ${C.border}`, padding: '72px 20px', opacity: 0 }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ ...syne(700), fontSize: 'clamp(20px, 3vw, 24px)', color: '#fff', textAlign: 'center', marginBottom: 32 }}>
            {t.faqTitle}
          </h2>
          {t.faq.map((item, i) => (
            <details key={i} style={{ borderBottom: `1px solid ${C.border}`, padding: '16px 0' }}>
              <summary style={{ ...sans(600), fontSize: 14, color: '#fff', cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {item.q}
                <span style={{ color: C.textSec, fontSize: 18, flexShrink: 0, marginLeft: 12 }}>+</span>
              </summary>
              <p style={{ ...sans(), fontSize: 13, color: C.textSec, lineHeight: 1.7, marginTop: 12, paddingRight: 24 }}>
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ background: C.footer, borderTop: `1px solid ${C.border}`, padding: '28px 20px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ ...syne(700), fontSize: 13, color: '#fff' }}>Your Brand Value Sports</div>
            <div style={{ ...mono(), fontSize: 9, color: C.blue, marginTop: 2 }}>{t.footerUrl}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ ...mono(), fontSize: 9, color: C.textSec }}>{t.footerProduct}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ ...mono(), fontSize: 8, color: C.dimBorder }}>{t.footerCopy}</div>
          </div>
        </div>
        <p style={{ ...sans(), fontSize: 9, color: '#2A2F36', maxWidth: 700, margin: '20px auto 0', lineHeight: 1.6, textAlign: 'center' }}>{t.disclaimer}</p>
      </footer>
    </div>
  );
}
